import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import {
  CheckCircle, XCircle, History, Clock, FileText,
  MousePointerClick, MailOpen, Loader2, X
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAdminEmail } from './AdminEmailContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  sent: '#28a745',
  skipped: '#ffa500',
  failed: '#dc3545',
};

const HistoryTab = () => {
  const ctx = useAdminEmail();
  const {
    historyFilter, handleHistoryFilterChange, emailCategories,
    announcements, formatDate,
  } = ctx;

  const [logsModal, setLogsModal] = useState(null); // { announcement, data } | null
  const [loadingLogs, setLoadingLogs] = useState(null);
  const [logFilter, setLogFilter] = useState('all'); // all | sent | skipped | failed

  const openLogs = async (ann) => {
    setLoadingLogs(ann.id);
    try {
      const res = await axios.get(`${API}/admin/email/announcements/${ann.id}/logs`, { withCredentials: true });
      setLogsModal({ announcement: ann, data: res.data });
      setLogFilter('all');
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Failed to load logs');
    } finally {
      setLoadingLogs(null);
    }
  };

  const filteredLogs = logsModal
    ? (logFilter === 'all' ? logsModal.data.logs : logsModal.data.logs.filter(l => l.status === logFilter))
    : [];

  return (
    <div className="history-tab">
      <Card className="history-card">
        <CardHeader>
          <CardTitle>Email History</CardTitle>
          <CardDescription>Previously sent announcements. Click "View Logs" on any row for the per-user trace of that run.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="history-filter">
            <label>Filter by Category:</label>
            <select
              value={historyFilter}
              onChange={(e) => handleHistoryFilterChange(e.target.value)}
              className="category-select"
              data-testid="history-filter"
            >
              <option value="all">All Categories</option>
              {emailCategories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.label}</option>
              ))}
            </select>
          </div>

          {announcements.length === 0 ? (
            <div className="empty-state">
              <History size={48} />
              <p>No announcements sent yet</p>
            </div>
          ) : (
            <div className="history-list">
              {announcements.map((ann, idx) => (
                <div key={idx} className="history-item" data-testid={`history-item-${idx}`}>
                  <div className="history-main">
                    <div className="history-title">{ann.title}</div>
                    <div className="history-subject">{ann.subject}</div>
                    <span className="category-badge">
                      {emailCategories.find(c => c.id === ann.category)?.label || 'Feature Announcement'}
                    </span>
                  </div>
                  <div className="history-stats">
                    <div className="history-stat">
                      <CheckCircle size={14} />
                      <span>{ann.sent || 0} sent</span>
                    </div>
                    {ann.failed > 0 && (
                      <div className="history-stat failed">
                        <XCircle size={14} />
                        <span>{ann.failed} failed</span>
                      </div>
                    )}
                  </div>
                  {ann.skip_breakdown && (ann.sent || 0) === 0 && (
                    <div className="history-skip-breakdown" data-testid={`skip-breakdown-${idx}`}>
                      <span className="skip-title">Why 0 sent:</span>
                      <span className="skip-pill">{ann.skip_breakdown.total_opted_in} opted-in</span>
                      {ann.skip_breakdown.no_upcoming_episodes > 0 && (
                        <span className="skip-pill">{ann.skip_breakdown.no_upcoming_episodes} no upcoming eps</span>
                      )}
                      {ann.skip_breakdown.no_eligible_shows > 0 && (
                        <span className="skip-pill">{ann.skip_breakdown.no_eligible_shows} no tracked shows</span>
                      )}
                      {ann.skip_breakdown.test_user > 0 && (
                        <span className="skip-pill">{ann.skip_breakdown.test_user} test user(s)</span>
                      )}
                      {ann.skip_breakdown.no_email > 0 && (
                        <span className="skip-pill">{ann.skip_breakdown.no_email} missing email</span>
                      )}
                    </div>
                  )}
                  <div className="tracking-stats">
                    <div className="tracking-stat opens" title="Open rate">
                      <MailOpen size={14} />
                      <span>{ann.tracking?.open_rate || 0}%</span>
                    </div>
                    <div className="tracking-stat clicks" title="Click rate">
                      <MousePointerClick size={14} />
                      <span>{ann.tracking?.click_rate || 0}%</span>
                    </div>
                  </div>
                  <div className="history-meta">
                    <Clock size={14} />
                    <span>{formatDate(ann.sent_at)}</span>
                  </div>
                  <span className={`status-badge ${ann.status}`}>
                    {ann.status}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openLogs(ann)}
                    disabled={loadingLogs === ann.id}
                    className="view-logs-btn"
                    data-testid={`view-logs-${idx}`}
                  >
                    {loadingLogs === ann.id ? (
                      <><Loader2 size={14} className="spinning" /> Loading...</>
                    ) : (
                      <><FileText size={14} /> View Logs</>
                    )}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {logsModal && (
        <div className="modal-overlay" onClick={() => setLogsModal(null)}>
          <div className="modal-content logs-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3>Run Log: {logsModal.announcement.title}</h3>
                <p className="logs-subtitle">{logsModal.announcement.subject}</p>
              </div>
              <button className="close-btn" onClick={() => setLogsModal(null)}>×</button>
            </div>

            <div className="logs-summary">
              <div className="logs-summary-item">
                <strong>{logsModal.data.total_steps}</strong> total steps
              </div>
              {Object.entries(logsModal.data.summary).map(([k, v]) => (
                <button
                  key={k}
                  className={`logs-summary-item filterable ${logFilter === k ? 'active' : ''}`}
                  style={{ borderColor: STATUS_COLORS[k], color: STATUS_COLORS[k] }}
                  onClick={() => setLogFilter(logFilter === k ? 'all' : k)}
                  data-testid={`logs-filter-${k}`}
                >
                  <strong>{v}</strong> {k}
                </button>
              ))}
              {logFilter !== 'all' && (
                <button className="logs-clear-filter" onClick={() => setLogFilter('all')}>
                  <X size={12} /> clear
                </button>
              )}
            </div>

            {Object.keys(logsModal.data.reason_counts || {}).length > 0 && (
              <div className="logs-reasons">
                {Object.entries(logsModal.data.reason_counts).map(([reason, count]) => (
                  <span key={reason} className="logs-reason-pill">
                    <code>{reason}</code> × {count}
                  </span>
                ))}
              </div>
            )}

            <div className="logs-list">
              {filteredLogs.length === 0 ? (
                <div className="empty-state small">
                  <p>No log entries{logFilter !== 'all' ? ` for status "${logFilter}"` : ''}.</p>
                  <p className="empty-hint">Per-user logging was added recently — older runs may not have logs.</p>
                </div>
              ) : (
                filteredLogs.map((log, i) => (
                  <div key={i} className={`log-row log-${log.status}`} data-testid={`log-row-${i}`}>
                    <span
                      className="log-status-dot"
                      style={{ background: STATUS_COLORS[log.status] || '#888' }}
                      title={log.status}
                    />
                    <div className="log-user">
                      <span className="log-name">{log.name || '(no name)'}</span>
                      <span className="log-email">{log.email || log.user_id || '(unknown user)'}</span>
                    </div>
                    <code className="log-reason">{log.reason || log.status}</code>
                    {log.details && Object.keys(log.details).length > 0 && (
                      <details className="log-details">
                        <summary>details</summary>
                        <pre>{JSON.stringify(log.details, null, 2)}</pre>
                      </details>
                    )}
                    <span className="log-time">{new Date(log.timestamp).toLocaleTimeString()}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HistoryTab;
